import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  HelpCircle,
  Shield,
  MessageSquare,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Zap,
  BookOpen,
  Mail,
} from "lucide-react";

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const Help: React.FC = () => {
  const [expandedFAQ, setExpandedFAQ] = useState<number | null>(null);

  const faqs: FAQItem[] = [
    {
      category: "Getting Started",
      question: "How do I connect my Facebook Page?",
      answer: "Go to 'Facebook Pages' in the sidebar, click 'Connect Page', and follow the authorization process. Make sure you have admin access to the Facebook Page you want to connect.",
    },
    {
      category: "Getting Started",
      question: "What permissions does the app need?",
      answer: "The app requires permissions to read and manage comments on your Facebook Page. This allows us to moderate comments and provide analytics. We never post without your explicit action.",
    },
    {
      category: "Moderation Rules",
      question: "What are Common Rules?",
      answer: "Common Rules are pre-built moderation rules for frequent scenarios like bad words, hate speech, spam, and emoji-only comments. You can activate them with one click and customize their actions (Hide or Delete).",
    },
    {
      category: "Moderation Rules",
      question: "What are Custom Rules?",
      answer: "Custom Rules allow you to create advanced moderation rules with custom triggers, conditions, and actions. You can set up rules based on keywords, sentiment analysis, or AI-powered detection.",
    },
    {
      category: "Moderation Rules",
      question: "What's the difference between Global and Post-specific rules?",
      answer: "Global rules apply to all posts on your Facebook Page, while Post-specific rules only apply to selected posts. This gives you flexibility to have different moderation strategies for different content.",
    },
    {
      category: "Moderation Rules",
      question: "How do I test my moderation rules?",
      answer: "Click the 'Test Moderation Rules' button at the top of the Moderation Rules page. Enter a test comment to see which rules would be triggered and what actions would be taken.",
    },
    {
      category: "Comments",
      question: "How do I view comments on my posts?",
      answer: "Navigate to the 'Comments' section from the sidebar. You can filter comments by post, status (approved, hidden, deleted), or search for specific content.",
    },
    {
      category: "Comments",
      question: "Can I manually moderate comments?",
      answer: "Yes! In the Comments section, you can manually hide, delete, or approve comments. Use the action menu next to each comment to perform these actions.",
    },
    {
      category: "Comment Insights",
      question: "What insights can I see?",
      answer: "Comment Insights provides analytics on sentiment (happy, angry, neutral), topic classification (price inquiries, product feedback, etc.), engagement types, trends over time, and top keywords mentioned in comments.",
    },
    {
      category: "Comment Insights",
      question: "How often are insights updated?",
      answer: "Insights are updated in real-time as new comments come in. You can also filter by date range to view historical data.",
    },
    {
      category: "Troubleshooting",
      question: "Comments are not syncing. What should I do?",
      answer: "First, check if your Facebook Page connection is still active in 'Facebook Pages'. If the connection expired, reconnect your page. If the issue persists, try refreshing the page or contact support.",
    },
    {
      category: "Troubleshooting",
      question: "Why can't I see all my posts?",
      answer: "Make sure you're selecting the correct Facebook Page from the page selector at the top. Also, check that the posts you're looking for fall within the selected date range filter.",
    },
  ];

  const features = [
    {
      icon: <Shield className="h-6 w-6 text-blue-600" />,
      title: "Moderation Rules",
      description: "Create automated rules to moderate comments based on keywords, sentiment, or AI detection.",
    },
    {
      icon: <MessageSquare className="h-6 w-6 text-green-600" />,
      title: "Comment Management",
      description: "View, filter, and manually moderate comments across all your Facebook posts in one place.",
    },
    {
      icon: <BarChart3 className="h-6 w-6 text-purple-600" />,
      title: "Comment Insights",
      description: "Get detailed analytics on comment sentiment, topics, engagement, and trends over time.",
    },
    {
      icon: <Zap className="h-6 w-6 text-amber-600" />,
      title: "Common Rules",
      description: "Quickly activate pre-built rules for common scenarios like profanity, spam, and hate speech.",
    },
  ];

  const categories = Array.from(new Set(faqs.map((faq) => faq.category)));

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-8"
        >
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="h-8 w-8 text-blue-600" />
            <h1 className="text-3xl font-bold text-gray-900">Help & Support</h1>
          </div>
          <p className="text-gray-600">
            Learn how to use Moujib to moderate and analyze comments on your Facebook Page
          </p>
        </motion.div>

        {/* Features Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-8"
        >
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-blue-600" />
                Key Features
              </CardTitle>
              <CardDescription>What you can do with Moujib</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {features.map((feature, index) => (
                  <div
                    key={index}
                    className="flex items-start gap-3 p-4 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex-shrink-0 mt-1">{feature.icon}</div>
                    <div>
                      <h3 className="font-semibold text-gray-900 mb-1">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-gray-600">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.2 }}
        >
          <Card className="border border-gray-200 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                Frequently Asked Questions
              </CardTitle>
              <CardDescription>
                Find answers to common questions about using Moujib
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {categories.map((category) => (
                  <div key={category}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">
                      {category}
                    </h3>
                    <div className="space-y-2">
                      {faqs
                        .filter((faq) => faq.category === category)
                        .map((faq, _index) => {
                          const globalIndex = faqs.indexOf(faq);
                          const isExpanded = expandedFAQ === globalIndex;
                          return (
                            <div
                              key={globalIndex}
                              className="border border-gray-200 rounded-lg overflow-hidden"
                            >
                              <button
                                onClick={() =>
                                  setExpandedFAQ(
                                    isExpanded ? null : globalIndex
                                  )
                                }
                                className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors"
                              >
                                <span className="font-medium text-gray-900 pr-4">
                                  {faq.question}
                                </span>
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-gray-500 flex-shrink-0" />
                                )}
                              </button>
                              {isExpanded && (
                                <motion.div
                                  initial={{ height: 0, opacity: 0 }}
                                  animate={{ height: "auto", opacity: 1 }}
                                  exit={{ height: 0, opacity: 0 }}
                                  transition={{ duration: 0.2 }}
                                  className="px-4 pb-4 pt-0"
                                >
                                  <p className="text-gray-600 text-sm leading-relaxed">
                                    {faq.answer}
                                  </p>
                                </motion.div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Contact Support */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, delay: 0.3 }}
          className="mt-8"
        >
          <Card className="border border-blue-200 bg-blue-50/50 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <Mail className="h-6 w-6 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Still need help?
                  </h3>
                  <p className="text-sm text-gray-600 mb-3">
                    Can't find the answer you're looking for? Our support team is here to help.
                  </p>
                  <a
                    href="mailto:support@moujib.com"
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Contact Support →
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default Help;

